import api, { type RequestOptions } from './api';
import type { ApiResponse, PaginatedResponse, SortField } from '../types';

interface VpnCounts {
  siteVpn: number;
  clientVpn: number;
}

interface DeviceConnectionCount {
  count: number;
}

interface DeviceRankingParams {
  pageNo: number;
  pageSize: number;
  sortBy: SortField;
  tenantId: string; // CRITICAL: Always required, even as empty string
}

interface DeviceRankingItem {
  id: number;
  dev_id: string;
  edgeName: string;
  customerName?: string;
  status: 'Online' | 'Offline' | 'Disconnect';
  cpu: string;
  memory: string;
  trafficSent: string;
  trafficReceived: string;
  tenantId?: string;
}

export const dashboardService = {
  /**
   * Get VPN counts (site and client)
   */
  getVpnCounts: async (tenantId: string, options?: RequestOptions): Promise<VpnCounts> => {
    const response = await api.get('/index/getVpns', {
      params: { tenantId }, // CRITICAL: Must send tenantId
      ...options
    });
    // The API interceptor returns response.data (the full API response)
    // The API returns { success: true, data: { siteVpn: { connected, disconnected }, clientVpn: { connected } } }
    // But the frontend expects { siteVpn: number, clientVpn: number }
    // Convert the response format to match frontend expectations
    const apiResponse = response as any;
    const data = apiResponse.data || apiResponse.result;
    if (data && typeof data === 'object') {
      return {
        siteVpn: typeof data.siteVpn === 'number' ? data.siteVpn : (data.siteVpn?.connected || 0) + (data.siteVpn?.disconnected || 0),
        clientVpn: typeof data.clientVpn === 'number' ? data.clientVpn : (data.clientVpn?.connected || 0),
      };
    }
    return { siteVpn: 0, clientVpn: 0 };
  },

  /**
   * Get device connection count
   */
  getDeviceConnectionCount: async (tenantId: string, options?: RequestOptions): Promise<DeviceConnectionCount> => {
    const response: ApiResponse<DeviceConnectionCount> = await api.get(
      '/index/deviceConnectionCount',
      {
        params: { tenantId },
        ...options
      }
    );
    // The API interceptor returns response.data (the full API response)
    // The API returns { success: true, data: { count: number } }
    // Extract the data field from the response
    const apiResponse = response as any;
    return apiResponse.data || apiResponse.result || { count: 0 };
  },

  /**
   * Get device ranking/sorting data
   */
  getDeviceRanking: async (
    params: DeviceRankingParams
  ): Promise<PaginatedResponse<DeviceRankingItem>> => {
    const response: ApiResponse<PaginatedResponse<DeviceRankingItem>> = await api.get(
      '/index/deviceSortBy',
      {
        params,
      }
    );
    return response.result;
  },
};

